import { Allocation } from "@/types/allocations.types";
import { ISubject } from "@/types/subject.types";
import {
  createContext,
  PropsWithChildren,
  useContext,
} from "react";
import { useQueries } from "@tanstack/react-query";
import { getClasses, getSubjects, getTerms } from "@/api/common";
import { getSubjectAllocations } from "@/api/teachers";
import { useLayout } from "@/hooks/useLayout";
import { ITerms } from "@/types/terms.types";
import { Class } from "@/types/class.types";
import useAuth from "@/hooks/useAuth";

type TMainLayoutContext = {
  subjects?: ISubject[];
  classes?: Class[];
  allocations?: Allocation[];
  terms?: ITerms[];
};

export const MainLayoutContext = createContext<TMainLayoutContext | undefined>(
  undefined,
);

const MainLayoutContextProvider = ({ children }: PropsWithChildren) => {
  const layout = useLayout();
  const { user, currentSessionId } = useAuth()
  const { isAuthenticated } = useAuth();

  const [
    { data: rawSubjects },
    { data: rawClasses },
    { data: rawAllocations },
    { data: rawTerms },
  ] = useQueries({
    queries: [
      {
        queryKey: ["subjects"],
        queryFn: getSubjects,
        suspense: true,
        enabled: isAuthenticated && layout !== "teacher",
      },
      {
        queryKey: ["classes"],
        queryFn: () => getClasses({}),
        suspense: true,
        enabled: isAuthenticated && layout === "admin",
      },

      {
        queryKey: ["subject-allocations"],
        queryFn: () => getSubjectAllocations({ teacher_id: user?.id, session_id: currentSessionId }),
        suspense: true,
        enabled: isAuthenticated && layout === "teacher",
      },
      {
        queryKey: ["terms"],
        queryFn: getTerms,
        suspense: true,
        enabled: isAuthenticated,
      },
    ],
  });

  const subjects = rawSubjects?.data;
  const classes = rawClasses?.data;
  const allocations = rawAllocations?.data;
  const terms = rawTerms?.data;


  return (
    <MainLayoutContext.Provider
      value={{
        subjects: subjects ?? [],
        classes: classes ?? [],
        allocations: allocations ?? [],
        terms: terms ?? [],

      }}
    >
      {children}
    </MainLayoutContext.Provider>
  );
};

export const useMainLayoutContext = () => {
  const context = useContext(MainLayoutContext);

  if (!context)
    throw new Error("useAuth must be used within an AuthContextProvider");

  return context;
};

export default MainLayoutContextProvider;
